import React, { useReducer } from 'react';
import { IconButton } from 'rsuite';
import { Icon } from '@rsuite/icons';
import { FaFileImport } from 'react-icons/fa';
import globalHandleError from '../../api/apiErrorHandle';
import FileUpload from '../FileUpload';
import PricingImportResultDialog from './PricingImportResultDialog';
import { PricingImportResult } from '../../api/apiTypes';

interface State {
  status: 'ready' | 'loading';
  result?: PricingImportResult;
}

const initialState: State = {
  status: 'ready',
  result: undefined,
};

type ImportLoadAction = { type: 'import/load' };
type ImportSetAction = { type: 'import/set', payload?: PricingImportResult };
type Action = ImportLoadAction | ImportSetAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'import/load':
      return { ...state, result: undefined, status: 'loading' };
    case 'import/set':
      return { ...state, result: action.payload, status: 'ready' };
    default:
      return state;
  }
}

interface Props {
  path: string;
  label: string;
}

export default function PricingFileImport({ path, label }: Readonly<Props>): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);

  function handleStart(): void {
    dispatch({ type: 'import/load' });
  }

  function handleSuccess(result: PricingImportResult): void {
    dispatch({ type: 'import/set', payload: result });
  }

  function handleError(error: Error): void {
    globalHandleError(error);
    dispatch({ type: 'import/set', payload: undefined });
  }

  function handleClose(): void {
    dispatch({ type: 'import/set', payload: undefined });
  }

  const isImporting = state.status === 'loading';

  return (
    <>
      <FileUpload<PricingImportResult>
        path={path}
        accept=".xlsx"
        disabled={isImporting}
        onStart={() => handleStart()}
        onError={(error) => handleError(error)}
        onSuccess={(result) => handleSuccess(result)}
      >
        <IconButton
          style={{ width: 220 }}
          appearance="primary"
          icon={<Icon as={FaFileImport} />}
          placement="right"
          loading={isImporting}
        >
          {label}
        </IconButton>
      </FileUpload>

      <PricingImportResultDialog
        response={state.result}
        onClose={() => handleClose()}
      />
    </>
  );
}
